import apiClient from '../../../config/api';

/** Estudio tal y como se envía al crear el lote. */
export interface BatchingStudyInput {
  id_paciente: string;
  descriptivo: string;
  mongo_template_id?: string | null;
  template_name?: string | null;
}

/** Estudio del lote: `studyId` lo asigna el backend y es estable. */
export interface BatchingStudy extends BatchingStudyInput {
  studyId: string;
}

export interface BatchingResponse {
  _id: string;
  userId: string;
  studies: BatchingStudy[];
  createdAt: string;
}

class BatchingService {
  /**
   * Crea un nuevo batching — guarda los estudios segmentados en el backend
   */
  async create(studies: BatchingStudyInput[]): Promise<BatchingResponse> {
    try {
      const response = await apiClient.post('/batching', { studies });
      return response.data.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Error al guardar el batching';
      throw new Error(errorMessage);
    }
  }

  /**
   * Obtiene todos los batchings del usuario autenticado
   */
  async getAll(): Promise<BatchingResponse[]> {
    try {
      const response = await apiClient.get('/batching');
      return response.data.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Error al obtener los batchings';
      throw new Error(errorMessage);
    }
  }

  /**
   * Obtiene un batching por su ID
   */
  async getById(id: string): Promise<BatchingResponse> {
    try {
      const response = await apiClient.get(`/batching/${id}`);
      return response.data.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Error al obtener el batching';
      throw new Error(errorMessage);
    }
  }

  /** Como `getById`, pero devuelve null si el lote ya no existe (se borró al enviar su último estudio). */
  async findById(id: string): Promise<BatchingResponse | null> {
    try {
      const response = await apiClient.get(`/batching/${id}`);
      return response.data.data;
    } catch (error: unknown) {
      const response = (error as { response?: { status?: number; data?: { message?: string } } }).response;
      if (response?.status === 404) return null;
      throw new Error(response?.data?.message || 'Error al obtener el batching');
    }
  }

  /**
   * Elimina un estudio del batching por su `studyId`.
   * Devuelve el batching actualizado o null si se eliminó el documento.
   */
  async removeStudy(batchId: string, studyId: string): Promise<BatchingResponse | null> {
    try {
      const response = await apiClient.delete(`/batching/${batchId}/study/${encodeURIComponent(studyId)}`);
      return response.data.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Error al eliminar el estudio del lote';
      throw new Error(errorMessage);
    }
  }
  /**
   * Elimina completamente un lote por su ID
   */
  async deleteBatch(batchId: string): Promise<void> {
    try {
      await apiClient.delete(`/batching/${batchId}`);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Error al eliminar el lote';
      throw new Error(errorMessage);
    }
  }
}

export const batchingService = new BatchingService();
